import { uid } from "@dhx/ts-common/core";
import { sv } from "@dhx/ts-common/dom";
import { getCoordinates, pieLikeHandlers } from "../helpers/circle";
import {
	checkPositions,
	getDefaultColor,
	getRadialGradient,
	getSizesSVGText,
	roundToTwoNumAfterPoint,
	verticalCenteredText,
} from "../helpers/common";
import { SvgElement } from "../types";
import NoScaleSeria from "./NoScaleSeria";

export default class Pie extends NoScaleSeria {
	// todo move to NoScaleSeria
	paint(width: number, height: number): SvgElement {
		const getPointAriaAttrs = (value, text, percent) => ({
			role: "graphics-symbol",
			"aria-roledescription": "piece of pie",
			"aria-label": `${text || ""}, ${value} (${roundToTwoNumAfterPoint(percent)}%)`,
		});
		const getChartAriaAttrs = cfg => ({
			"aria-label": `chart ${cfg.text || ""}`,
		});

		const { stroke, strokeWidth, gradient, useLines, showText, showTextTemplate, subType } = this.config;
		const defaultStrokeWidth = !strokeWidth || strokeWidth < 1 ? 4 : strokeWidth > 15 ? 15 : strokeWidth;
		let radius;
		if (height > width) {
			radius = width / 2;
		} else {
			radius = height / 2;
		}
		let currentPercent = -0.25; // 0 percent is (1, 0) point, -.25 is (0, -1) point
		const textPoints = [];

		const tooltipData = [];

		let svg = [];

		const defs = [];
		const pie = [];
		const lines = [];
		if (this._points.length > 1 && stroke) {
			svg.push(
				sv("circle", {
					cx: 0,
					cy: 0,
					r: radius - 0.5,
					fill: stroke,
				})
			);
		}
		this._points.forEach((item, index) => {
			const link = [];
			const [percent, value, id, text, color] = item;
			const err = percent === 0 || percent === 1 ? -0.000001 : 0;
			let fill: any = color || getDefaultColor(index);
			if (gradient) {
				const grad = gradient(color as any);
				const gradientId = "gradient" + uid();
				const radialGradient = getRadialGradient(grad.options, grad.stops, gradientId);
				fill = `url(#${gradientId})`;
				defs.push(radialGradient);
			}
			const [startX, startY] = getCoordinates(
				currentPercent,
				radius,
				radius,
				this._points.length > 1 && stroke ? defaultStrokeWidth / 2 : null
			);
			const avPercent = currentPercent + percent / 2;
			let lineLength = avPercent < 0.25 ? 5 : -5; // from 0 to 180 right pointer, 180 to 360 left pointer
			const middleLine = getCoordinates(avPercent, radius, radius);
			currentPercent += percent + err;
			const [endX, endY] = getCoordinates(
				currentPercent,
				radius,
				radius,
				this._points.length > 1 && stroke ? -defaultStrokeWidth / 2 : null
			);
			const largeArcFlag = percent > 0.5 ? 1 : 0;
			const [startPart, endPart] = [5, 30];
			const [linkStart, linkEnd] = [
				getCoordinates(avPercent, radius + startPart, radius + startPart),
				getCoordinates(avPercent, radius + endPart, radius + endPart),
			];
			const isRight = avPercent > -0.25 && avPercent < 0.25;
			const isUp = avPercent > 0.5 || avPercent < 0;
			const className =
				avPercent > -0.25 && avPercent < 0.25 ? "pie-value start-text" : "pie-value end-text";

			if ((showText || showTextTemplate) && showText !== false) {
				const linkText = sv(
					"text",
					{
						x: middleLine[0] * 0.7,
						y: middleLine[1] * 0.7,
						class: "pie-inner-value",
						"aria-hidden": "true",
					},
					[
						showTextTemplate
							? verticalCenteredText(showTextTemplate(value.toString()))
							: verticalCenteredText(value.toString()),
					]
				);
				link.push(linkText);
			}

			switch (this.config.subType) {
				case "basic": {
					const textPadding = 10;
					const dy = isUp ? -textPadding * 2 : textPadding;
					const linkStart = getCoordinates(avPercent, radius + 10, radius + 10);
					const className2 = isRight ? "donut-value start-text" : "donut-value end-text";

					const currentText = {
						text1: {
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy,
							width: 0,
							height: 0,
							class: "",
						},
						text2: {
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy + 16,
							width: 0,
							height: 0,
							class: "",
						},
						changeSector: false,
						line: lineLength,
						right: isRight,
						dy: dy,
					};

					const text1 = sv(
						"text",
						{
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy,
							class: className,
							...getPointAriaAttrs(value, text, percent),
						},
						[verticalCenteredText(text.toString())]
					);
					const text2 = sv(
						"text",
						{
							x: useLines ? linkEnd[0] : linkStart[0],
							y: (useLines ? linkEnd[1] : linkStart[1]) + dy + 16,
							class: className2,
							"aria-hidden": "true",
						},
						[verticalCenteredText(value.toString())]
					);

					[currentText.text1.width, currentText.text1.height] = getSizesSVGText(text.toString(), {
						font: "normal 14px Roboto",
						lineHeight: 14,
					});
					[currentText.text2.width, currentText.text2.height] = getSizesSVGText(value.toString(), {
						font: "normal 12px Roboto",
						lineHeight: 12,
					});

					const textRadius = useLines ? radius + endPart : radius + 10;

					if (textPoints.length !== 0) {
						if (isRight) {
							textPoints.forEach(previosText => {
								checkPositions(
									currentText.text1,
									previosText.text1,
									textRadius,
									textRadius,
									currentText
								);
								checkPositions(
									currentText.text1,
									previosText.text2,
									textRadius,
									textRadius,
									currentText
								);
							});
							if (currentText.text1.class) currentText.text2.class = currentText.text1.class;
						} else {
							textPoints.forEach(previosText => {
								checkPositions(
									currentText.text2,
									previosText.text2,
									textRadius,
									textRadius,
									currentText
								);
								checkPositions(
									currentText.text2,
									previosText.text1,
									textRadius,
									textRadius,
									currentText
								);
							});
							if (currentText.text2.class) currentText.text1.class = currentText.text2.class;
						}
						text1.attrs.x = currentText.text1.x;
						text1.attrs.y = currentText.text1.y;
						text2.attrs.x = currentText.text2.x;
						text2.attrs.y = currentText.text2.y;
						if (currentText.text1.class || currentText.text2.class) {
							text1.attrs.class = currentText.text1.class;
							text2.attrs.class = currentText.text2.class;
						}
						lineLength = currentText.line;

						if (useLines) {
							linkEnd[0] = currentText.text1.x;
							linkEnd[1] = currentText.text1.y - dy;
						} else {
							linkStart[0] = currentText.text1.x;
							linkStart[1] = currentText.text1.y - dy;
						}
					}

					textPoints.push(currentText);

					const text3 = sv(
						"text",
						{
							x: middleLine[0] * 0.5,
							y: middleLine[1] * 0.5,
							class: "pie-inner-value",
							"aria-hidden": "true",
						},
						[verticalCenteredText(Math.round(percent * 100) + "%")]
					);
					link.push(
						sv(
							"g",
							{
								id: `${id}-text`,
								class: "chart donut",
							},
							[text1, text2]
						)
					);
					link.push(text3);
					break;
				}
				case "valueOnly": {
					const valueText = sv(
						"text",
						{
							x: middleLine[0] * 0.5,
							y: middleLine[1] * 0.5,
							class: "pie-inner-value",
							"aria-hidden": "true",
						},
						[verticalCenteredText(`${value}`)]
					);
					link.push(valueText);
					break;
				}
				case "percentOnly": {
					const percentText = sv(
						"text",
						{
							x: middleLine[0] * 0.5,
							y: middleLine[1] * 0.5,
							class: "pie-inner-value",
							"aria-hidden": "true",
						},
						[verticalCenteredText(Math.round(percent * 100) + "%")]
					);
					link.push(percentText);
					break;
				}
			}

			if (useLines && subType === "basic") {
				const dy = isUp ? 3 : 0;
				link.push(
					sv("path", {
						d: `M${linkStart[0]} ${linkStart[1]} L${linkEnd[0]}
							${linkEnd[1] + dy} h ${lineLength}`,
						id: `${id}-connector`,
						class: "pie-value-connector chart pie",
					})
				);
			}

			let centerX = 0,
				centerY = 0;
			const pseudoRadius = defaultStrokeWidth / (2 * Math.sin(Math.PI / this._points.length));
			if (this._points.length > 1 && stroke) {
				[centerX, centerY] = getCoordinates(avPercent, pseudoRadius, pseudoRadius);
			}
			const d = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
				L ${0 + centerX} ${0 + centerY}`;
			const [shiftX, shiftY] = getCoordinates(avPercent, 4, 4);

			const currentSector = sv("path", {
				d,
				class: "chart pie",
				_key: id,
				fill,
				onclick: [this._handlers.onclick, item[1], item[2]],
				// eslint-disable-next-line @typescript-eslint/unbound-method
				onmouseover: [pieLikeHandlers.onmouseover, shiftX, shiftY],
				// eslint-disable-next-line @typescript-eslint/unbound-method
				onmouseout: [pieLikeHandlers.onmouseout],
				role: "presentation",
			});
			link.unshift(currentSector);
			pie.push(sv("g", { id: id }, link));
			if (this._points.length === 1) {
				tooltipData.push([width / 2, height / 2]);
			} else {
				tooltipData.push([middleLine[0] * 0.7 + width / 2, middleLine[1] * 0.7 + height / 2]);
			}
		});

		this._center = [width / 2, height / 2];
		this._tooltipData = tooltipData;

		svg.push(sv("defs", defs));
		svg = svg.concat(pie);
		svg = svg.concat(lines);
		return sv(
			"g",
			{
				transform: `translate(${width / 2}, ${height / 2})`,
				...getChartAriaAttrs(this.config),
				tabindex: 0,
			},
			svg
		);
	}
}
